import { FragmentHandler } from '@seek/oauth-url-builders';
import type { LoginOptions } from '@seek/online-identity';

import { Experience } from 'src/types';
import { getLanguagePrefixedPath } from 'src/utils/getLanguage';
import getSearchParams from 'src/utils/getSearchParams';
import getCookies from 'src/utils/hirer/getCookies';
import { clearAdvertiserContext } from 'src/utils/hirer/setAdvertiserContext';
import loginMessage from 'src/utils/loginMessage';
import {
  validateReturnPath,
  validateReturnUri,
} from 'src/utils/validateReturnUri';

import type { PreLoginHook } from '../types';

import { recordAnalytics } from './pre-login-helpers';

const hirerPreLoginHook: PreLoginHook = async (
  { authN, analytics },
  inputPath = '/',
  language = 'en',
  locale = 'au',
) => {
  const searchParams = getSearchParams();
  const languagePrefix = getLanguagePrefixedPath(language);
  const accountRegistrationPath = `${languagePrefix}/account/registration`;
  const accountSelectionPath = `${languagePrefix}/account/select`;

  const path =
    searchParams.returnPath === accountRegistrationPath
      ? '/register'
      : inputPath;

  const defaultReturnPath = accountSelectionPath;

  const { flow, success, message, ReturnUrl, error_description } = searchParams;
  const login_msg =
    typeof flow === 'string'
      ? loginMessage(flow, success !== 'false', message as string)
      : error_description;

  const { da_cdt } = getCookies();
  const fragment = FragmentHandler.stringify(path, {
    email_hint:
      typeof searchParams.email === 'string' ? searchParams.email : undefined,
    locale,
    language,
    login_msg,
    da_cdt,
  });

  const returnPath =
    searchParams.returnPath === '/'
      ? defaultReturnPath
      : validateReturnPath(searchParams.returnPath as string);

  const options: LoginOptions = {
    returnUri:
      typeof ReturnUrl === 'string'
        ? validateReturnUri(ReturnUrl, language, Experience.Hirer)
        : returnPath,
    fragment,
    additionalArguments: { language: `${language}-${locale.toUpperCase()}` },
  };

  if (error_description) {
    // flush the oi__scopes to prevent the unfortunate user getting stuck in a loop
    authN.scopeHandler.clear();

    options.additionalArguments = {
      ...options.additionalArguments,
      prompt: 'login',
    };
  }
  await recordAnalytics(searchParams, analytics);

  clearAdvertiserContext();
  authN.login(options);

  return false;
};

export default hirerPreLoginHook;
